import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProgressSummary = ({
  currentChapter = 2,
  totalChapters = 8,
  overallProgress = 35,
  completedChapters = [
    { id: 1, title: "Introduction to AI", score: 92, completed: true },
    { id: 2, title: "Machine Learning Basics", score: 85, completed: true }
  ],
  nextChapter = {
    id: 3,
    title: "Neural Networks",
    unlocked: true,
    estimatedTime: "45 minutes"
  },
  achievements = [
    { id: 1, title: "First Quiz Completed", icon: "Award", earned: true },
    { id: 2, title: "Perfect Score", icon: "Trophy", earned: false },
    { id: 3, title: "Speed Learner", icon: "Zap", earned: true },
    { id: 4, title: "Consistent Performer", icon: "Target", earned: false }
  ]
}) => {
  const getChapterStatusIcon = (chapter) => {
    if (chapter.completed) {
      return chapter.score >= 90 ? "Trophy" : "CheckCircle2";
    }
    return "Circle";
  };

  const getChapterStatusColor = (chapter) => {
    if (chapter.completed) {
      return chapter.score >= 90 ? "var(--color-accent)" : "var(--color-success)";
    }
    return "var(--color-text-muted)";
  };

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="p-6 border bg-surface rounded-xl border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold font-heading text-text-primary">
            Learning Progress
          </h2>
          <div className="flex items-center px-3 py-2 space-x-2 rounded-lg bg-success-50">
            <Icon name="TrendingUp" size={16} color="var(--color-success)" strokeWidth={2} />
            <span className="text-sm font-medium text-success-700">
              {overallProgress}% Complete
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">
              Chapter {currentChapter} of {totalChapters}
            </span>
            <span className="font-medium text-text-primary">
              {overallProgress}%
            </span>
          </div>
          
          <div className="w-full h-3 rounded-full bg-border-light">
            <div 
              className="h-3 rounded-full bg-gradient-to-r from-success to-success-600 progress-animation"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Chapter Completion Status */}
      <div className="p-6 border bg-surface rounded-xl border-border">
        <h3 className="mb-6 text-xl font-bold font-heading text-text-primary">
          Chapter Completion Status
        </h3>
        
        <div className="space-y-4">
          {completedChapters.map((chapter) => (
            <div key={chapter.id} className="flex items-center p-4 space-x-4 rounded-lg bg-surface-secondary">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-success-50">
                <Icon 
                  name={getChapterStatusIcon(chapter)} 
                  size={20} 
                  color={getChapterStatusColor(chapter)}
                  strokeWidth={2}
                />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold font-heading text-text-primary">
                    Chapter {chapter.id}: {chapter.title}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <span className={`
                      px-2 py-1 rounded-full text-xs font-medium
                      ${chapter.score >= 90 
                        ? 'bg-accent-100 text-accent-800' :'bg-success-100 text-success-800'}
                    `}>
                      {chapter.score}%
                    </span>
                    <Icon name="CheckCircle2" size={16} color="var(--color-success)" strokeWidth={2} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Next Chapter */}
      {nextChapter.unlocked && (
        <div className="p-6 border bg-gradient-to-r from-secondary-50 to-primary-50 rounded-xl border-secondary-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold font-heading text-text-primary">
              Continue Your Journey
            </h3>
            <div className="flex items-center px-3 py-2 space-x-2 rounded-lg bg-secondary-100">
              <Icon name="Unlock" size={16} color="var(--color-secondary)" strokeWidth={2} />
              <span className="text-sm font-medium text-secondary-700">Unlocked</span>
            </div>
          </div>
          
          <div className="flex items-center mb-4 space-x-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-secondary-100">
              <Icon name="BookOpen" size={24} color="var(--color-secondary)" strokeWidth={2} />
            </div>
            <div>
              <h4 className="text-lg font-semibold font-heading text-text-primary">
                Chapter {nextChapter.id}: {nextChapter.title}
              </h4>
              <p className="text-text-secondary">
                Estimated time: {nextChapter.estimatedTime}
              </p>
            </div>
          </div>
          
          <Link to="/interactive-presentation-viewer">
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              className="spring-bounce"
            >
              Start Chapter {nextChapter.id}
            </Button>
          </Link>
        </div>
      )}

      {/* Achievements */}
      <div className="p-6 border bg-surface rounded-xl border-border">
        <h3 className="mb-6 text-xl font-bold font-heading text-text-primary">
          Achievements
        </h3>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {achievements.map((achievement) => (
            <div 
              key={achievement.id} 
              className={`
                flex items-center space-x-3 p-4 rounded-lg border transition-all duration-200
                ${achievement.earned 
                  ? 'border-accent-200 bg-accent-50' :'border-border bg-surface-secondary opacity-60'}
              `}
            >
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-lg
                ${achievement.earned ? 'bg-accent-100' : 'bg-surface'}
              `}>
                <Icon 
                  name={achievement.icon} 
                  size={20} 
                  color={achievement.earned ? "var(--color-accent)" : "var(--color-text-muted)"}
                  strokeWidth={2}
                />
              </div>
              
              <div className="flex-1">
                <h4 className={`
                  font-heading font-semibold
                  ${achievement.earned ? 'text-accent-800' : 'text-text-muted'}
                `}>
                  {achievement.title}
                </h4>
                <p className={`
                  text-sm
                  ${achievement.earned ? 'text-accent-600' : 'text-text-muted'}
                `}>
                  {achievement.earned ? 'Earned' : 'Not earned yet'}
                </p>
              </div>
              
              {achievement.earned && (
                <Icon name="CheckCircle2" size={16} color="var(--color-accent)" strokeWidth={2} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      {/* <div className="p-6 border bg-surface rounded-xl border-border">
        <h3 className="mb-4 text-xl font-bold font-heading text-text-primary">
          Quick Actions
        </h3>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Link to="/learning-progress-tracker">
            <Button
              variant="outline"
              size="md"
              fullWidth
              iconName="BarChart3"
              iconPosition="left"
            >
              View Full Progress
            </Button>
          </Link>
          
          <Link to="/chapter-overview-dashboard">
            <Button
              variant="ghost"
              size="md"
              fullWidth
              iconName="Home"
              iconPosition="left"
            >
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div> */}
    </div>
  );
};

export default ProgressSummary;